import { useMemo } from 'react';
import type { GitBookFrameProps } from '@gitbook/embed/react';

import { useXFetch } from '~/shared/utils/useXFetch';
import type {
  LastDayResponse,
  SavingStatsResponse,
  VolumeResponse,
  VolumesAutoCompleteResponse,
} from '~/shared/types/api';

type GitBookTool = NonNullable<GitBookFrameProps['tools']>[number];
type ToolExecuteResult = {
  output: object;
  summary?: { icon: string; text: string };
};
type XFetch = ReturnType<typeof useXFetch>;

function toolSummary(text: string): NonNullable<ToolExecuteResult['summary']> {
  return { icon: 'circle-info', text };
}

async function runTool(
  failureText: string,
  fn: () => Promise<ToolExecuteResult>
): Promise<ToolExecuteResult> {
  try {
    return await fn();
  } catch {
    return {
      output: { error: true },
      summary: toolSummary(failureText),
    };
  }
}

async function autocompleteVolumeIds(xfetch: XFetch, pattern: string): Promise<string[]> {
  const fields = ['volumeId', 'instanceId'] as const;
  const responses = await Promise.all(
    fields.map((field) => {
      const qs = new URLSearchParams({ field, pattern });
      return xfetch<VolumesAutoCompleteResponse>(`/api/v1/volumes/autocomplete?${qs}`);
    })
  );
  return [...new Set(responses.flatMap((response) => response.values ?? []))].slice(0, 10);
}

async function fetchVolumesByIds(xfetch: XFetch, volumeIds: string[]): Promise<VolumeResponse> {
  const qs = new URLSearchParams({ limit: String(volumeIds.length) });
  volumeIds.forEach((id) => {
    qs.append(
      'filters',
      JSON.stringify({ field: 'volumeId', value: id, op: 'equals' })
    );
  });
  return xfetch<VolumeResponse>(`/api/v1/volumes?${qs.toString()}`);
}

export function useDatafyTools(): GitBookTool[] {
  const xfetch = useXFetch();

  return useMemo<GitBookTool[]>(
    () => [
      {
        name: 'get_monthly_savings',
        description:
          "Get the current estimated monthly storage cost, potential cost after further optimization, and realized monthly savings for the user's Datafy-managed EBS volumes. Use when the user asks about cost, spend, savings, or optimization potential.",
        inputSchema: { type: 'object', properties: {} },
        execute: async () =>
          runTool('Unable to fetch savings data right now. Please try again.', async () => {
            const data = await xfetch<SavingStatsResponse>('/api/v1/volumes/savings-stats');
            return {
              output: data,
              summary: toolSummary(
                `Current monthly cost $${data.CurrentEstimatedMonthlyCost}, savings $${data.currentEstimatedMonthlySavings}, potential additional savings $${data.PotentialEstimatedMonthlySaving}.`
              ),
            };
          }),
      },
      {
        name: 'get_recent_activity',
        description:
          'Get a summary of Datafy autoscale activity in the last 24 hours (number of grow actions, shrink actions, and average utilization) versus the previous 24 hours. Use when the user asks what happened recently, today, or in the last day.',
        inputSchema: { type: 'object', properties: {} },
        execute: async () =>
          runTool('Unable to fetch recent activity right now. Please try again.', async () => {
            const data = await xfetch<LastDayResponse>('/api/v1/opt-log/last-day');
            return {
              output: data,
              summary: toolSummary(
                `Last 24h: ${data.lastDay.growthActions} grow, ${data.lastDay.shrinkActions} shrink, avg utilization ${data.lastDay.avgUtilization}%.`
              ),
            };
          }),
      },
      {
        name: 'search_volumes',
        description:
          "List up to 10 of the user's Datafy-monitored volumes. When a query is provided, match volume or instance IDs via the volumes autocomplete API, then return matching volume records.",
        inputSchema: {
          type: 'object',
          properties: {
            query: {
              type: 'string',
              description:
                'Partial volume or instance ID to search for. Omit to list the first 10 volumes.',
            },
          },
        },
        execute: async (input: object) => {
          const { query } = input as { query?: string };
          return runTool('Unable to search volumes right now. Please try again.', async () => {
            const trimmedQuery = query?.trim();

            if (!trimmedQuery) {
              const qs = new URLSearchParams({ limit: '10' });
              const data = await xfetch<VolumeResponse>(`/api/v1/volumes?${qs.toString()}`);
              return {
                output: data,
                summary: toolSummary(`${data.volumes?.length ?? 0} volume(s) returned.`),
              };
            }

            const matchedIds = await autocompleteVolumeIds(xfetch, trimmedQuery);
            if (matchedIds.length === 0) {
              return {
                output: { volumes: [], matchedIds: [] },
                summary: toolSummary(`No volumes matched "${trimmedQuery}".`),
              };
            }

            const data = await fetchVolumesByIds(xfetch, matchedIds);
            return {
              output: data,
              summary: toolSummary(
                `${data.volumes?.length ?? 0} volume(s) returned for "${trimmedQuery}".`
              ),
            };
          });
        },
      },
    ] as GitBookTool[],
    [xfetch]
  );
}
